/**
 * VideoTemplateGrid
 *
 * Masonry grid for video templates with hover-to-play cards and responsive
 * columns. Accepts saved state and skeleton placeholders.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Heart, Play, Download, Eye, Clock, Tag, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './MasonryLayout.css';

function VideoTemplateCard({ videoTemplate, onVideoTemplateClick, onSaveVideoTemplate, isSaved, onImageLoaded, isPremiumUser = true }) {
  const [isHovered, setIsHovered] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [generatedThumbnail, setGeneratedThumbnail] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isImagePreloaded, setIsImagePreloaded] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const navigate = useNavigate();

  // Convert Firebase Storage URLs
  const convertFirebaseStorageUrl = (gsUrl) => {
    if (!gsUrl) return gsUrl;
    
    // If it's already an HTTP URL (signed URL from backend), return as-is
    if (gsUrl.startsWith('http://') || gsUrl.startsWith('https://')) {
      return gsUrl;
    }
    
    // Only convert gs:// URLs (for backward compatibility)
    if (!gsUrl.startsWith('gs://')) {
      return gsUrl; // Return as-is if not a gs:// URL
    }
    
    // Extract bucket and path from gs://bucket/path
    const match = gsUrl.match(/^gs:\/\/([^\/]+)\/(.+)$/);
    if (!match) {
      return gsUrl; // Return as-is if format doesn't match
    }
    
    const [, bucket, path] = match;
    // Convert to Firebase Storage HTTP URL
    return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media`;
  };

  const convertedVideoUrl = convertFirebaseStorageUrl(videoTemplate.videoUrl);

  // Preload video for immediate playback
  useEffect(() => {
    if (videoRef.current && convertedVideoUrl) {
      // Load video metadata and buffer for immediate playback
      videoRef.current.load();
      // Prepare video for faster playback by starting to buffer
      const prepareVideo = async () => {
        try {
          if (videoRef.current) {
            // Set to start of video and prepare for immediate play
            videoRef.current.currentTime = 0;
            // Don't actually play yet, just prepare
          }
        } catch (error) {
          // Ignore errors during preparation
        }
      };
      prepareVideo();
    }
  }, [convertedVideoUrl]);

  // Generate thumbnail from video if no thumbnail provided
  useEffect(() => {
    if (!videoTemplate.thumbnailUrl && convertedVideoUrl && !generatedThumbnail && !thumbnailError) {
      generateThumbnailFromVideo();
    }
  }, [videoTemplate.thumbnailUrl, convertedVideoUrl, generatedThumbnail, thumbnailError]);

  // Preload thumbnail image
  useEffect(() => {
    const thumbnailUrl = videoTemplate.thumbnailUrl || generatedThumbnail;
    if (thumbnailUrl) {
      const img = new Image();
      img.src = thumbnailUrl;
      img.onload = () => {
        setIsImagePreloaded(true);
      };
      img.onerror = () => {
        setThumbnailError(true);
        setIsImagePreloaded(true);
      };
    }
  }, [videoTemplate.thumbnailUrl, generatedThumbnail]);

  const generateThumbnailFromVideo = async () => {
    try {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      
      const thumbnailPromise = new Promise((resolve, reject) => {
        video.onloadeddata = () => {
          try {
            const seekTime = Math.min(video.duration * 0.25, 0.5);
            video.currentTime = seekTime;
          } catch (err) {
            reject(new Error('Failed to seek video'));
          }
        };
        
        video.onseeked = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            
            const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.8);
            resolve(thumbnailDataUrl);
            
            video.pause();
            if (video.src.startsWith('blob:')) {
              URL.revokeObjectURL(video.src);
            }
          } catch (err) {
            reject(new Error('Failed to generate thumbnail'));
          }
        };
        
        video.onerror = () => {
          reject(new Error('Failed to load video'));
          if (video.src.startsWith('blob:')) {
            URL.revokeObjectURL(video.src);
          }
        };
      });

      video.src = convertedVideoUrl;
      video.load();

      const thumbnailUrl = await thumbnailPromise;
      setGeneratedThumbnail(thumbnailUrl);
    } catch (error) {
      console.error('Error generating thumbnail:', error);
      setThumbnailError(true);
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Slight delay helps avoid quick enter/leave causing AbortError
    hoverTimeoutRef.current = setTimeout(() => {
      if (videoRef.current && convertedVideoUrl) {
        try {
          videoRef.current.currentTime = 0;
          const playPromise = videoRef.current.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch((error) => {
              // Ignore AbortError (play interrupted by a pause), do not mark as error
              const message = String(error?.message || error || '');
              if (error?.name === 'AbortError' || message.includes('pause()')) return;
              console.log('Video play failed:', error);
              setVideoError(true);
            });
          }
        } catch (error) {
          const message = String(error?.message || error || '');
          if (error?.name === 'AbortError' || message.includes('pause()')) return;
          setVideoError(true);
        }
      }
    }, 80);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    
    // Clear hover timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Pause video
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      } catch (_) {}
    }
  };

  const handleCanvaClick = (e) => {
    e.stopPropagation();
    
    if (!videoTemplate.canvaLink) return;
    
    // For premium users, open Canva link directly
    if (isPremiumUser) {
      window.open(videoTemplate.canvaLink, '_blank');
      return;
    }
    
    // For basic users, redirect to pricing page
    navigate('/app/account', { state: { fromTemplates: true } });
  };

  const handleCardClick = () => {
    // All users can view video templates
    onVideoTemplateClick(videoTemplate);
  };

  const handleSaveClick = (e) => {
    e.stopPropagation();
    if (onSaveVideoTemplate) {
      onSaveVideoTemplate(videoTemplate.id, !isSaved);
    }
  };

  const handleImageLoad = () => {
    if (!imageLoaded) {
      setImageLoaded(true);
      if (onImageLoaded) {
        onImageLoaded();
      }
    }
  };

  const handleImageError = () => {
    console.error(`Failed to load thumbnail for video template: ${videoTemplate.id}`);
    setThumbnailError(true);
    setImageLoaded(true);
    if (onImageLoaded) {
      onImageLoaded();
    }
  };

  // Force image loading check after a timeout to prevent hanging cards
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!imageLoaded) {
        console.log(`Timeout loading thumbnail for video template: ${videoTemplate.id}`);
        setImageLoaded(true);
        if (onImageLoaded) {
          onImageLoaded();
        }
      }
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [videoTemplate.id, imageLoaded, onImageLoaded]);

  // Clean up timeouts
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const thumbnailSrc = videoTemplate.thumbnailUrl || generatedThumbnail;

  return (
    <div 
      className="group relative transition-all duration-200 ease-in-out cursor-pointer"
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Video/Thumbnail container */}
      <div className="relative overflow-hidden rounded-lg">
        {/* Skeleton loader shown while image is loading */}
        {!isImagePreloaded && (
          <div className="bg-gray-200 dark:bg-gray-700 animate-pulse w-full aspect-video">
          </div>
        )}

        {/* Video - show for all users */}
        {convertedVideoUrl && (
          <video
            ref={videoRef}
            className={`absolute inset-0 w-full h-full object-cover z-10 ${
              isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            style={{ transition: 'opacity 0.2s ease-in-out' }}
            crossOrigin="anonymous"
            muted
            playsInline
            loop
            preload="auto"
            onError={() => setVideoError(true)}
            onLoadedMetadata={() => setVideoReady(true)}
          >
            <source src={convertedVideoUrl} type="video/mp4" />
          </video>
        )}
        
        {/* Thumbnail */}
        {!thumbnailError && thumbnailSrc ? (
          <div 
            className={`relative ${isImagePreloaded ? 'opacity-100' : 'opacity-0'} ${
              isHovered && convertedVideoUrl && !videoError ? 'opacity-0' : 'opacity-100'
            }`} 
            style={{ 
              transition: 'opacity 0.2s ease-in-out',
              visibility: isImagePreloaded ? 'visible' : 'hidden' 
            }}
          >
            <img
              src={thumbnailSrc}
              alt={videoTemplate.title}
              className="w-full h-auto display-block"
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          </div>
        ) : (
          <div className="bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-center p-4 aspect-video">
            <Play className="w-8 h-8 text-gray-400" />
          </div>
        )}
        
        {/* Overlay gradient that only appears on hover - with higher z-index */}
        {isHovered && imageLoaded && (
          <div 
            className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10 z-20 animate-fadeIn"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
          ></div>
        )}
        
        {/* Canva logo in the top left - appears on hover */}
        {isHovered && imageLoaded && videoTemplate.canvaLink && (
          <button 
            onClick={handleCanvaClick}
            className="absolute top-2 left-2 sm:top-3 sm:left-3 z-30 animate-fadeIn"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
            aria-label="Edit in Canva"
          >
            <div className="h-8 w-8 sm:h-10 sm:w-10 flex items-center justify-center hover:scale-110 transition-transform duration-150">
              <img 
                src="/images/canva.png" 
                alt="Open in Canva" 
                className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
              />
            </div>
          </button>
        )}
        
        {/* Save button in top right - appears on hover or when saved */}
        {onSaveVideoTemplate && (isHovered || isSaved) && imageLoaded && (
          <div className="absolute top-2 right-2 sm:top-4 sm:right-4 z-30 animate-fadeIn"
               style={{ animation: 'fadeIn 0.2s ease-out' }}>
            <button
              onClick={handleSaveClick}
              className={`p-2 sm:p-2.5 rounded-full shadow-sm hover:shadow-md transition-all duration-150 hover:scale-110 ${
                isSaved 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-white text-gray-700'
              }`}
              aria-label={isSaved ? "Remove from saved" : "Save template"}
            >
              <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${isSaved ? 'fill-white' : ''}`} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function VideoTemplateGrid({ 
  videoTemplates = [], 
  onVideoTemplateClick, 
  onSaveVideoTemplate,
  savedVideoTemplateIds = new Set(),
  loading = false, 
  skeletonItems = [],
  isPremiumUser = true
}) {
  const [columns, setColumns] = useState(4); // Default to 4 columns
  const gridRef = useRef(null);
  const [loadedImages, setLoadedImages] = useState(new Set());

  // Set up columns based on screen size
  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width < 1024) {
        setColumns(2); // Below lg: 2 columns
      } else {
        setColumns(3); // lg and up: 3 columns
      }
    };

    // Initial column setup
    updateColumns();

    // Update columns on resize
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  // Track image loading progress
  useEffect(() => {
    if (videoTemplates.length === 0) return;
    
    // Reset loaded images when videoTemplates change
    setLoadedImages(new Set());
  }, [videoTemplates]);

  // Handle image load events from VideoTemplateCard
  const handleImageLoaded = (videoTemplateId) => {
    setLoadedImages(prev => {
      const newSet = new Set(prev);
      newSet.add(videoTemplateId);
      return newSet;
    });
  };

  if (videoTemplates.length === 0 && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
          <Play className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No video templates found</h3>
        <p className="text-gray-500 dark:text-gray-400 max-w-md">
          Try adjusting your search criteria or filters to find the video templates you're looking for.
        </p>
      </div>
    );
  }

  // Distribute video templates across columns for masonry layout
  const distributeVideoTemplates = () => {
    const columnArrays = Array.from({ length: columns }, () => []);
    
    videoTemplates.forEach((videoTemplate, index) => {
      const columnIndex = index % columns;
      columnArrays[columnIndex].push(videoTemplate);
    });
    
    return columnArrays;
  };
  
  const columnArrays = distributeVideoTemplates();

  return (
    <div className="masonry-wrapper">
      <div className="masonry-grid" ref={gridRef}>
        {columnArrays.map((columnVideoTemplates, columnIndex) => (
          <div 
            key={`column-${columnIndex}`} 
            className="masonry-column"
            style={{ width: `${100 / columns}%` }}
          >
            {columnVideoTemplates.map((videoTemplate) => (
              <div 
                key={videoTemplate.id} 
                className="masonry-item sm:p-1"
              >
                <div className="masonry-item-inner">
                  <VideoTemplateCard 
                    videoTemplate={videoTemplate} 
                    onVideoTemplateClick={onVideoTemplateClick}
                    onSaveVideoTemplate={onSaveVideoTemplate}
                    isSaved={savedVideoTemplateIds.has(videoTemplate.id)}
                    onImageLoaded={() => handleImageLoaded(videoTemplate.id)}
                    isPremiumUser={isPremiumUser}
                  />
                </div>
              </div>
            ))}
            
            {/* Skeleton items for loading */}
            {loading && skeletonItems.map((_, index) => (
              <div 
                key={`skeleton-${columnIndex}-${index}`} 
                className="masonry-item sm:p-1"
              >
                <div className="masonry-item-inner bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse">
                  <div className="w-full aspect-video bg-gradient-to-b from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 rounded-sm"></div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default VideoTemplateGrid; 